import Cookies from "js-cookie";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getAuthorityByUser } from "../services/apiService";
import { resetUserData, setUserData } from "../redux/slices/userDataSlice";
import { getCookie } from "./getCookie";

const UNAUTHORIZED_PATH = "/unauthorized";

export const clearSession = (dispatch) => {
  Cookies.remove("sesion");
  if (dispatch) dispatch(resetUserData());
};

export const clearSessionAndRedirect = (dispatch, navigate) => {
  clearSession(dispatch);

  if (navigate) {
    navigate(UNAUTHORIZED_PATH, { replace: true });
    return;
  }

  if (window.location.pathname !== UNAUTHORIZED_PATH) {
    window.location.replace(UNAUTHORIZED_PATH);
  }
};

export const getSessionUserId = () => {
  const userSesion = getCookie("sesion");
  return userSesion?.id_usuario || null;
};

export const isInactiveSessionError = (error) => {
  const status = error?.response?.status;
  const message = String(error?.response?.data?.error || "").toLowerCase();

  return (
    status === 401 ||
    status === 403 ||
    message.includes("inactivo") ||
    message.includes("inexistente")
  );
};

export const useActiveSession = ({ loadAuthorities = false } = {}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUserData = useSelector((state) => state.userData.userData);
  const userSesion = getCookie("sesion");
  const userId = userSesion?.id_usuario || null;
  const isLegacySession = Boolean(
    currentUserData?.legacyPending ||
      userSesion?.legacyPending ||
      currentUserData?.authSource === "LEGACY" ||
      userSesion?.authSource === "LEGACY"
  );
  const [isCheckingSession, setIsCheckingSession] = useState(loadAuthorities);

  const loadUserAuthorities = useCallback(async () => {
    if (!userId) {
      clearSessionAndRedirect(dispatch, navigate);
      return false;
    }

    try {
      const response = await getAuthorityByUser(userId, {
        legacy: isLegacySession,
      });
      const data = response.data;
      dispatch(
        setUserData({
          id: userId,
          authorityList: data.authorityList || [],
          pageList: data.pageList || [],
          roleList: data.roleIds || [],
          authSource: currentUserData?.authSource || userSesion?.authSource || "INTERNAL",
          state: currentUserData?.state || userSesion?.state || null,
          legacyPending: Boolean(
            currentUserData?.legacyPending || userSesion?.legacyPending
          ),
        })
      );
      return true;
    } catch (error) {
      if (isInactiveSessionError(error)) {
        clearSessionAndRedirect(dispatch, navigate);
        return false;
      }
      throw error;
    }
  }, [
    currentUserData?.authSource,
    currentUserData?.legacyPending,
    currentUserData?.state,
    dispatch,
    isLegacySession,
    navigate,
    userId,
    userSesion?.authSource,
    userSesion?.legacyPending,
    userSesion?.state,
  ]);

  useEffect(() => {
    let mounted = true;

    if (!loadAuthorities) {
      setIsCheckingSession(false);
      return undefined;
    }

    const checkSession = async () => {
      try {
        await loadUserAuthorities();
      } catch (error) {
        clearSessionAndRedirect(dispatch, navigate);
      } finally {
        if (mounted) setIsCheckingSession(false);
      }
    };

    checkSession();

    return () => {
      mounted = false;
    };
  }, [dispatch, loadAuthorities, loadUserAuthorities, navigate]);

  return {
    isCheckingSession,
    loadUserAuthorities,
    userId
  };
};
